"use client";

import React, { useMemo } from "react";
import PricePredictionChart from "@/src/components/DetailsCommon/Forecast/PricePrediction/PricePredictionChart";
import {
  useStockDetailsMarket,
  useStockDetailsHandle,
  useTokenDetailsMarket,
  useTokenDetailsHandle,
} from "@/src/hooks";
import { FORECAST_QUERY_PARAMS } from "@config/stock";
import calculateTimeRange from "@/src/utils/calculateTimeRange";
import {
  ViewDetailsLink,
  buildStockDetailModuleHref,
  buildCryptoDetailHref,
} from "./ViewDetailsLink";

interface AssetPriceForecastBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetPriceForecastBusinessChart: React.FC<
  AssetPriceForecastBusinessChartProps
> = ({ assetType, assetRouterId }) => {
  const isStock = assetType === "stock";

  const cryptoQueryParams = useMemo(() => {
    const { startTime, endTime } = calculateTimeRange("180d");
    return {
      "enrich.enable": true,
      "enrich.with_ohlc.enable": true,
      "enrich.with_ohlc.start_time": startTime,
      "enrich.with_ohlc.end_time": endTime,
      "enrich.with_ohlc.interval": "1d",
      "enrich.with_rating": true,
    };
  }, []);

  const { data: stockData, isLoading: stockLoading } = useStockDetailsMarket({
    enabled: isStock,
    queryParams: { ...FORECAST_QUERY_PARAMS, ids: [assetRouterId] },
    queryKey: ["chat-forecast", assetRouterId],
  });

  const { data: tokenList, isLoading: tokenLoading } = useTokenDetailsMarket({
    enabled: !isStock,
    ids: [assetRouterId],
    queryParams: cryptoQueryParams,
  });

  const isLoading = isStock ? stockLoading : tokenLoading;
  const stockSymbol = stockData?.symbol || "";

  const stockHandle = useStockDetailsHandle({
    tokenList: isStock ? stockData : undefined,
    symbol: stockSymbol,
  });

  const tokenHandle = useTokenDetailsHandle({
    tokenList: !isStock ? tokenList : undefined,
  });

  const targetPrice = isStock
    ? stockHandle.targetPrice
    : tokenHandle.targetPrice;
  const forecastLineData = isStock
    ? stockHandle.forecastLineData
    : tokenHandle.forecastLineData;
  const logo = isStock ? stockHandle.logo : tokenHandle.logo;
  const displaySymbol = isStock
    ? stockSymbol
    : tokenHandle.symbol || assetRouterId;

  const { low, high, mean, last_updated } = targetPrice || {};
  const forecast = useMemo(() => {
    if (!low || !high || !mean) {
      return undefined;
    }
    return { low: Number(low), high: Number(high), avg: Number(mean) };
  }, [low, high, mean]);

  if (isLoading) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!targetPrice && !forecastLineData?.XAxis?.length) {
    return null;
  }

  const detailHref = isStock
    ? buildStockDetailModuleHref(stockSymbol, "forecast")
    : buildCryptoDetailHref(assetRouterId);

  return (
    <>
      <div className="w-full overflow-hidden">
        <PricePredictionChart
          XAxis={forecastLineData.XAxis}
          YAxis={forecastLineData.YAxis}
          ticks={forecastLineData.ticks}
          last_updated={last_updated}
          type={isStock ? "stock" : "token"}
          forecast={forecast}
          name={displaySymbol}
          logo={logo}
        />
      </div>
      <ViewDetailsLink href={detailHref} />
    </>
  );
};
